import * as pdfjsLib from 'pdfjs-dist';
import { PDFDocument } from 'pdf-lib';
import type { DocumentInitParameters } from 'pdfjs-dist/types/src/display/api';

// PDF.js worker を設定
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

// CMap・標準フォントの配信元（#3397）
// 日本語フォントを埋め込まずに参照するPDF（UniJIS-UCS2-H・90ms-RKSJ-H 等）は、
// CMap が無いとフォントを読み込めず、罫線だけ描画されて文字がすべて消える。
// cdnjs は cmaps を配信していないため、npm パッケージをそのまま配信する jsDelivr を使う。
const PDFJS_ASSET_BASE_URL = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}`;

/**
 * getDocument に渡す読み込みオプションを返す
 * @param data PDFのバイナリ
 */
const buildDocumentParams = (data: ArrayBuffer): DocumentInitParameters => ({
  data,
  cMapUrl: `${PDFJS_ASSET_BASE_URL}/cmaps/`,
  cMapPacked: true,
  standardFontDataUrl: `${PDFJS_ASSET_BASE_URL}/standard_fonts/`,
});

export interface ConvertedImage {
  dataUrl: string;
  width: number;
  height: number;
  page: number;
}

/**
 * PDFファイルを画像に変換する（300DPIに相当）
 * @param file PDFファイル
 * @param scale スケール（デフォルト: 4.17 = 300DPI相当）
 * @returns 変換された画像データの配列
 */
export const convertPdfToImages = async (file: File, scale = 4.17): Promise<ConvertedImage[]> => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument(buildDocumentParams(arrayBuffer)).promise;
    const images: ConvertedImage[] = [];
    const numPages = pdf.numPages;

    // 全ページを変換
    for (let pageNum = 1; pageNum <= numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale: scale });

      // Canvas要素を作成
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      if (!context) {
        throw new Error('Canvas context could not be created');
      }

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      // PDFページをCanvasにレンダリング
      const renderContext = {
        canvasContext: context,
        viewport: viewport,
      };

      await page.render(renderContext).promise;

      // CanvasをDataURLに変換
      const dataUrl = canvas.toDataURL('image/png');

      images.push({
        dataUrl,
        width: viewport.width,
        height: viewport.height,
        page: pageNum,
      });
    }

    return images;
  } catch (error) {
    throw new Error(
      `PDF変換に失敗しました: ${error instanceof Error ? error.message : 'Unknown error'}`,
    );
  }
};

/**
 * ファイルがPDFかどうかを判定する関数
 * @param file チェックするファイル
 * @returns PDFファイルの場合true
 */
export const isPdfFile = (file: File): boolean => {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
};

/**
 * PDFファイルのページ数を取得する（軽量版）
 * splitPdfIntoPagesと異なりページの実データを生成しないため高速
 * @param file PDFファイル
 * @returns ページ数
 */
export const getPdfPageCount = async (file: File): Promise<number> => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument(buildDocumentParams(arrayBuffer)).promise;
    return pdf.numPages;
  } catch {
    return 1;
  }
};

/**
 * URLからPDFのページ数を取得する（既存ファイル用）
 * @param url PDFファイルのURL
 * @returns ページ数
 */
export const getPdfPageCountFromUrl = async (url: string): Promise<number> => {
  try {
    const response = await fetch(url);
    const arrayBuffer = await response.arrayBuffer();
    const pdf = await pdfjsLib.getDocument(buildDocumentParams(arrayBuffer)).promise;
    return pdf.numPages;
  } catch {
    return 1;
  }
};

// splitPdfIntoPages で生成した分割ファイルと、その兄弟ファイル群を管理する
const splitFileRegistry = new WeakMap<File, File[]>();

/**
 * ファイルが splitPdfIntoPages で生成された分割ファイルかどうかを返す
 */
export const isSplitPageFile = (file: File): boolean => splitFileRegistry.has(file);

/**
 * 分割ファイルと同じ元PDFから生成された兄弟ファイル群を返す（自身を含む）
 * 分割ファイルでない場合は [file] を返す
 */
export const getSplitSiblings = (file: File): File[] => splitFileRegistry.get(file) ?? [file];

/**
 * PDFを個別のページに分割する関数
 * @param file 元のPDFファイル
 * @returns 分割されたPDFファイルの配列
 */
export const splitPdfIntoPages = async (file: File): Promise<File[]> => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const originalPdf = await PDFDocument.load(arrayBuffer);
    const pageCount = originalPdf.getPageCount();

    const splitFiles: File[] = [];

    if (pageCount === 1) {
      return [file];
    }

    const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
    const extension = file.name.match(/\.[^/.]+$/)?.[0] || '.pdf';

    for (let pageIndex = 0; pageIndex < pageCount; pageIndex++) {
      const newPdf = await PDFDocument.create();
      const [copiedPage] = await newPdf.copyPages(originalPdf, [pageIndex]);
      newPdf.addPage(copiedPage);
      const pdfBytes = await newPdf.save();
      const pageFileName = `${nameWithoutExt}_${pageIndex + 1}${extension}`;
      const pageFile = new File([new Uint8Array(pdfBytes)], pageFileName, {
        type: 'application/pdf',
        lastModified: file.lastModified,
      });
      splitFiles.push(pageFile);
    }

    for (const pageFile of splitFiles) {
      splitFileRegistry.set(pageFile, splitFiles);
    }

    return splitFiles;
  } catch (error) {
    throw new Error('PDFの分割中にエラーが発生しました。');
  }
};

/**
 * ファイルリスト内で、指定インデックスのファイルが
 * 同一グループ（同じ元PDFから分割）の最初の要素かどうかを返す。
 */
export const isFirstInSplitGroup = (files: File[], index: number): boolean => {
  const file = files[index];
  if (!isSplitPageFile(file)) return true;
  const siblings = getSplitSiblings(file);
  for (let i = 0; i < index; i++) {
    if (siblings.includes(files[i])) return false;
  }
  return true;
};

/**
 * ファイルリスト内で、指定インデックスのファイルが属するグループ全体のインデックス配列を返す。
 */
export const getSplitGroupIndices = (files: File[], index: number): number[] => {
  const file = files[index];
  if (!isSplitPageFile(file)) return [index];
  const siblings = getSplitSiblings(file);
  return files.map((f, i) => (siblings.includes(f) ? i : -1)).filter(i => i !== -1);
};

/**
 * ファイル配列の合計ページ数を計算する
 * PDFファイル → ページ数、画像ファイル → 1ページ
 * @param files ファイル配列
 * @returns 合計ページ数
 */
export const calculateTotalPageCount = async (files: File[]): Promise<number> => {
  let totalPages = 0;
  for (const file of files) {
    if (isPdfFile(file)) {
      totalPages += await getPdfPageCount(file);
    } else {
      totalPages += 1;
    }
  }
  return totalPages;
};

/**
 * PDFを先頭 maxPages ページだけの新しいPDFファイルに切り詰める
 * ページ数が maxPages 以下の場合は元ファイルをそのまま返す
 * @param file 元のPDFファイル
 * @param maxPages 残すページ数（1以上）
 * @returns 先頭ページのみの新しいPDFファイル
 */
export const truncatePdfToPages = async (file: File, maxPages: number): Promise<File> => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const originalPdf = await PDFDocument.load(arrayBuffer);
    const pageCount = originalPdf.getPageCount();
    if (pageCount <= maxPages) return file;

    const newPdf = await PDFDocument.create();
    const pageIndices = Array.from({ length: maxPages }, (_, i) => i);
    const copiedPages = await newPdf.copyPages(originalPdf, pageIndices);
    copiedPages.forEach(page => newPdf.addPage(page));
    const pdfBytes = await newPdf.save();

    return new File([new Uint8Array(pdfBytes)], file.name, {
      type: 'application/pdf',
      lastModified: file.lastModified,
    });
  } catch (error) {
    throw new Error('PDFの切り詰め中にエラーが発生しました。');
  }
};

/**
 * ファイル配列を合計 maxPages ページに収まるよう先頭から切り詰める。
 * - PDFはページ数、画像は1ページ換算で合計を数える
 * - 境界にかかったPDFは truncatePdfToPages で先頭ページのみに切り詰める
 * - 予算を超えたファイルは除外する
 * @param files ファイル配列（表示順）
 * @param maxPages 合計ページ上限
 * @param alreadyUsedPages 既に消費済みのページ数（編集時の既存サンプル分など）。残り予算 = maxPages - alreadyUsedPages
 * @returns 切り詰め後のファイル配列と、切り落としが発生したかのフラグ
 */
export const capFilesToTotalPages = async (
  files: File[],
  maxPages: number,
  alreadyUsedPages = 0,
): Promise<{ files: File[]; truncated: boolean }> => {
  const result: File[] = [];
  let usedPages = alreadyUsedPages;
  let truncated = false;

  for (const file of files) {
    const remaining = maxPages - usedPages;
    if (remaining <= 0) {
      truncated = true;
      continue;
    }
    if (isPdfFile(file)) {
      const pageCount = await getPdfPageCount(file);
      if (pageCount <= remaining) {
        result.push(file);
        usedPages += pageCount;
      } else {
        result.push(await truncatePdfToPages(file, remaining));
        usedPages += remaining;
        truncated = true;
      }
    } else {
      // 画像は1ページ換算
      result.push(file);
      usedPages += 1;
    }
  }

  return { files: result, truncated };
};
